// Управление сессией пользователя (JWT / sessionKey / userId).
// Источник истины — localStorage.currentSession
import { rootStore } from "@/store/index"
import { authStore } from "@/store/login"
import { clearTagsCache } from "@/composables/useTagsCache"

const SESSION_KEY = "currentSession"

export type StoredSession = {
  id: number
  sessionKey: string
  jwtToken: string
}

/** Читаем сохранённую сессию из localStorage. */
export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && parsed.jwtToken) {
      return {
        id: parsed.id,
        sessionKey: parsed.sessionKey,
        jwtToken: parsed.jwtToken,
      }
    }
    return null
  } catch (e) {
    return null
  }
}

/** Наполняем стору данными сессии */
export function applySession(session: StoredSession): void {
  const auth = authStore()
  auth.setUserId(session.id)
  auth.setSessionKey(session.sessionKey)
  auth.setJWT(session.jwtToken)
  rootStore().setToken(session.jwtToken)
}

/** Полностью очищаем сессию: localStorage и стору. */
export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
  const auth = authStore()
  auth.currentUserId = null
  auth.sessionKey = null
  auth.jwtToken = null
  auth.currentWorker = null
  rootStore().setToken("")
  clearTagsCache()
}

/**
 * Выход из аккаунта: гасим сессию и уводим на /login.
 *
 * Роутер импортируется лениво — статический импорт замкнёт цикл, router/index.ts
 * импортирует этот модуль. Вызовов может прилететь несколько подряд: параллельные
 * 401 плюс объявление от Angular-гостя, потерявшего авторизацию. Проверка текущего
 * роута такую гонку не ловит, потому что она асинхронная, — отсюда флаг.
 */
let loggingOut = false

export function logout(options: { rememberRoute?: boolean } = {}): void {
  if (loggingOut) {
    return
  }

  loggingOut = true
  import("@/router")
    .then((routerModule) => {
      const router = routerModule.default
      if (router.currentRoute.value.path === "/login") {
        return
      }
      const redirect = router.currentRoute.value.fullPath
      clearSession()
      const query = options.rememberRoute === false
          ? {}
          : { redirect: redirect }
      return router.push({ path: "/login", query: query })
    })
    .catch((e) => console.error(e))
    .finally(() => {
      loggingOut = false
    })
}
